import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { Modal, Button } from '../../components';
import css from './LinkModal.module.css';
import IconCard from '../SavedCardDetails/IconCard/IconCard';

const LinkModal = ({ isOpen, onClose, onConfirm, onManageDisableScrolling, initialText = '' }) => {
  const intl = useIntl();
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setUrl('');
      setText(initialText || '');
      setError('');
    }
  }, [isOpen, initialText]);

  const formatUrl = inputUrl => {
    const trimmed = (inputUrl || '').trim();
    if (!trimmed) return '';
    // If it's already an absolute protocol or root relative path, keep it
    if (/^(https?:\/\/|mailto:|tel:|\/|#)/i.test(trimmed)) {
      return trimmed;
    }
    // Otherwise automatically prepend https://
    return `https://${trimmed}`;
  };

  const handleClose = () => {
    setUrl('');
    setText('');
    setError('');
    onClose();
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    
    const formatted = formatUrl(url);
    if (!formatted) {
      setError('Please enter a URL');
      return;
    }

    try {
      if (!formatted.startsWith('/') && !formatted.startsWith('#')) {
        new URL(formatted);
      }
      onConfirm({ url: formatted, text: text.trim() });
      handleClose();
    } catch (err) {
      setError('Please enter a valid URL (e.g., https://example.com)');
    }
  };

  if (!isOpen) return null;

  const textPlaceholder = intl.formatMessage({
    id: 'FieldRichTextEditor.linkModal.textPlaceholder',
    defaultMessage: 'e.g., Click here',
  });

  return (
    <Modal
      id="link-modal"
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
      containerClassName={css.modalContainer}
      contentClassName={css.modalContent}
    >
      <div className={css.modalHeader}>
        <h2 className={css.modalTitle}>
          <FormattedMessage id="FieldRichTextEditor.linkModal.title" defaultMessage="Insert link" />
        </h2>
        <button type="button" className={css.closeButton} onClick={handleClose}>
          <IconCard brand="close" />
        </button>
      </div>
      
      <div role="form" className={css.form}>
        <div className={css.formRow}>
          <label htmlFor="link-url" className={css.label}>
            <FormattedMessage 
              id="FieldRichTextEditor.linkModal.urlLabel" 
              defaultMessage="URL" 
            />
          </label>
          <input
            id="link-url"
            type="url"
            className={classNames(css.input, { [css.inputError]: error })}
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              if (error) setError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            placeholder="https://example.com"
            autoFocus
          />
          {error && <div className={css.error}>{error}</div>}
        </div>

        <div className={css.formRow}>
          <label htmlFor="link-text" className={css.label}>
            <FormattedMessage 
              id="FieldRichTextEditor.linkModal.textLabel" 
              defaultMessage="Text to display (optional)" 
            />
          </label>
          <input
            id="link-text"
            type="text"
            className={css.input}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            placeholder={textPlaceholder}
          />
        </div>
        
        <div className={css.actions}>
          <Button
            type="button"
            onClick={handleClose}
            rootClassName={css.cancelButton}
          >
            <FormattedMessage id="FieldRichTextEditor.linkModal.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            rootClassName={css.submitButton}
          >
            <FormattedMessage id="FieldRichTextEditor.linkModal.insert" defaultMessage="Insert link" />
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default LinkModal;

